import React from "react";
import { ArrowRight, Heart, Sparkles, ImagePlus } from "lucide-react";
import { SITE } from "../lib/site";
import { useSite } from "../lib/SiteContext";

function HeroImage({ src, label, rotate, classes, testid }) {
  const hasImg = !!src;
  return (
    <div
      data-testid={testid}
      className={`absolute rounded-3xl overflow-hidden border-4 border-white shadow-xl transition-transform duration-500 ${classes}`}
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      {hasImg ? (
        <img src={src} alt={label} className="w-full h-full object-cover" loading="eager" />
      ) : (
        <div className="w-full h-full bg-[#EAE5DF] flex flex-col items-center justify-center text-[#A8A29E] p-4 text-center">
          <ImagePlus className="h-8 w-8 mb-2" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold">
            {label}
          </span>
        </div>
      )}
    </div>
  );
}

export default function Hero() {
  const { config } = useSite();
  const [img1 = "", img2 = "", img3 = ""] = config.hero_images || [];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-10 md:pt-16 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: Copy */}
          <div className="lg:col-span-7 rise">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#EAE5DF] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#57534E]">
              <Sparkles className="h-3.5 w-3.5 text-[#D85C41]" strokeWidth={2.5} />
              A Hamilton Community Initiative
            </div>

            <h1 className="mt-6 font-display text-5xl sm:text-6xl md:text-7xl font-extrabold leading-[1.02] tracking-tight text-[#1C1917]">
              Clean clothes,{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#D85C41]">restored</span>
                <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#EBB04D]/40 -z-0 rounded-sm" />
              </span>{" "}
              dignity for our children.
            </h1>

            <p className="mt-6 max-w-xl text-base md:text-lg text-[#57534E] leading-relaxed">
              {SITE.name} is a free laundromat program for displaced children and
              families in Hamilton. No cost. No questions. Just warm clothes,
              warm hearts, and a fair shot at school, work, and a good night's
              sleep.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#request"
                data-testid="hero-request-help-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D85C41] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#BD4C33] transition-all duration-300 hover:-translate-y-0.5 shadow-md shadow-[#D85C41]/20"
              >
                Request Help
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
              <a
                href={config.contact?.donate_url || SITE.donateUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-donate-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent border-2 border-[#1C1917] px-7 py-3.5 text-sm font-semibold text-[#1C1917] hover:bg-[#1C1917] hover:text-white transition-all duration-300 hover:-translate-y-0.5"
              >
                <Heart className="h-4 w-4" strokeWidth={2.5} />
                Donate via Venmo
              </a>
            </div>

            <div className="mt-10 flex items-center gap-6 text-sm text-[#78716C]">
              <div className="flex items-center -space-x-2">
                <span className="h-8 w-8 rounded-full bg-[#EBB04D] border-2 border-[#F9F7F4]" />
                <span className="h-8 w-8 rounded-full bg-[#9EA992] border-2 border-[#F9F7F4]" />
                <span className="h-8 w-8 rounded-full bg-[#D85C41] border-2 border-[#F9F7F4]" />
              </div>
              <span>Built by neighbors, for neighbors — across Hamilton.</span>
            </div>
          </div>

          {/* Right: Image collage */}
          <div className="lg:col-span-5 relative h-[460px] sm:h-[540px] md:h-[600px]">
            <HeroImage
              src={img1}
              label="Photo 1"
              rotate={-3}
              classes="top-0 left-2 sm:left-6 w-[62%] h-[62%]"
              testid="hero-image-1"
            />
            <HeroImage
              src={img2}
              label="Photo 2"
              rotate={4}
              classes="bottom-4 right-0 w-[58%] h-[55%]"
              testid="hero-image-2"
            />
            <div className="hidden sm:block">
              <HeroImage
                src={img3}
                label="Photo 3"
                rotate={-6}
                classes="bottom-0 left-0 w-[34%] h-[34%]"
                testid="hero-image-3"
              />
            </div>
            <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#EBB04D]/30 blur-2xl -z-10" />
            <div className="absolute -bottom-8 -left-4 w-40 h-40 rounded-full bg-[#D85C41]/20 blur-3xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
}
